import { useState, type ReactNode } from 'react';
import { UMIcon } from './UMIcon';
import { UMCheckbox } from './UMSidePanel';
import { UMBadge, UMButton, UMSourceInfo } from './UMForm';
import { UMR } from './referenceTokens';

/**
 * Building record ("fiche bâtiment") opened by clicking a building on the map.
 * Production: Fluid v5 `fluid-modal-side--s` docked on the right (300px, overlay on the map),
 * grey `zoom_out_map` expand tab, information badge with the functional id, edit / close actions.
 * Spec: docs/urbanmap/reference/panels.md. Data below is a typed shape for mocks — never real owners.
 */

export interface UMDataPoint<T> {
  value: T;
  /** Source line of the tooltip, e.g. "ADEME - Observatoire DPE-AUDIT (arrêté 2021) - 04/2026". */
  source?: string;
  /** Orange info icon (low confidence value). */
  warning?: boolean;
  verified?: boolean;
}

export interface UMHeatUse {
  energy: UMDataPoint<string>;
  mode: UMDataPoint<'Individuel' | 'Collectif' | 'Inconnu'>;
  /** MWhu */
  consumption: number;
}

export interface UMBuildingDetail {
  /** e.g. "BFR690376329" */
  functionalId: string;
  address: string;
  /** Folders (sélections) the building belongs to. */
  selections: string[];
  heating: UMHeatUse;
  hotWater: UMHeatUse;
  /** MWhu */
  cooling: number;
  usages: UMDataPoint<{ label: string; share: number }[]>;
  totalArea: UMDataPoint<number>;
  storeys: UMDataPoint<number>;
  dwellings: UMDataPoint<number>;
  constructionYear: UMDataPoint<number>;
  owner: UMDataPoint<string>;
  ownerType: UMDataPoint<'Public' | 'Privé' | 'Inconnu'>;
  syndic: UMDataPoint<string>;
  dataVersion: string;
}

const num = (n: number, digits = 1) => n.toLocaleString('fr-FR', { maximumFractionDigits: digits, minimumFractionDigits: digits > 0 && n % 1 ? 1 : 0 });

const card = { border: `1px solid ${UMR.border}`, borderRadius: 8, padding: 8 } as const;
const caption = { fontSize: 12, lineHeight: UMR.lhXs, color: UMR.textSecondary } as const;

function Value({ value, unit }: { value: string; unit: string }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontWeight: 700 }}>{value}</div>
      <div style={caption}>{unit}</div>
    </div>
  );
}

function Info({ point }: { point: UMDataPoint<unknown> }) {
  const [verified, setVerified] = useState(!!point.verified);
  if (!point.source) return null;
  return <UMSourceInfo source={point.source} warning={point.warning} verified={verified} onVerifiedChange={setVerified} />;
}

function HeatRow({ label, use }: { label: string; use: UMHeatUse }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <div style={caption}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontWeight: 700 }}>{use.energy.value}</span>
          <Info point={use.energy} />
          <span style={{ fontWeight: 700 }}>{use.mode.value}</span>
          <Info point={use.mode} />
        </div>
      </div>
      <Value value={num(use.consumption)} unit="MWhu" />
    </div>
  );
}

function SimpleCard({ title, point, span = 1, children }: { title: string; point: UMDataPoint<unknown>; span?: 1 | 2; children: ReactNode }) {
  return (
    <div style={{ ...card, gridColumn: `span ${span}` }}>
      {/* prod `.card-item .header`: title top-aligned (scoped CSS, real 16px line-height, not Tailwind text-xs), info 4px lower, 4px gap below. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4, minHeight: 21, marginBottom: 4 }}>
        <div style={{ ...caption, lineHeight: '16px' }}>{title}</div>
        <div style={{ marginTop: 4, display: 'flex' }}>
          <Info point={point} />
        </div>
      </div>
      <div style={{ fontWeight: 700, lineHeight: '20px', wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}

function OwnerLine({ title, point }: { title: string; point: UMDataPoint<string> }) {
  return (
    <div>
      {/* 21px comes from the info popover button: no source → 15px header, as in prod. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, minHeight: point.source ? 21 : undefined }}>
        <div style={caption}>{title}</div>
        <Info point={point} />
      </div>
      <div style={{ fontWeight: 700, wordBreak: 'break-word' }}>{point.value}</div>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <div style={{ fontWeight: 700, fontSize: 16, lineHeight: '24px', paddingBottom: 4, borderBottom: `1px solid ${UMR.border}` }}>{children}</div>;
}

export interface UMBuildingDetailPanelProps {
  building: UMBuildingDetail;
  onClose: () => void;
  onEdit?: () => void;
  /** Grey `zoom_out_map` tab: production opens the full-screen building view. */
  onExpand?: () => void;
  /** All user folders, for the "Présent dans" dropdown. */
  availableSelections?: string[];
  onSelectionsChange?: (selections: string[]) => void;
  onStreetView?: () => void;
  onEarth?: () => void;
}

export function UMBuildingDetailPanel({
  building: b,
  onClose,
  onEdit,
  onExpand,
  availableSelections = [],
  onSelectionsChange,
  onStreetView,
  onEarth,
}: UMBuildingDetailPanelProps) {
  const [open, setOpen] = useState(false);
  const heatTotal = b.heating.consumption + b.hotWater.consumption;
  return (
    <aside
      aria-label={`Bâtiment ${b.functionalId}`}
      data-testid="show-building-modal"
      style={{
        position: 'relative', width: 300, height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
        background: UMR.white, borderLeft: `1px solid ${UMR.border}`, fontFamily: UMR.font, fontSize: 14, lineHeight: '21px', color: UMR.black,
      }}
    >
      {onExpand && (
        <button
          type="button"
          title="Voir plus d'informations"
          aria-label="Voir plus d'informations"
          onClick={onExpand}
          style={{ position: 'absolute', top: 16, left: -32, width: 32, height: 32, border: 'none', padding: 0, borderRadius: '4px 0 0 4px', background: UMR.borderInput, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <UMIcon name="zoom_out_map" size={20} color={UMR.black} />
        </button>
      )}
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, padding: 16, height: 64, boxSizing: 'border-box' }}>
        <UMBadge variant="information" icon="flag">{b.functionalId}</UMBadge>
        <div style={{ display: 'flex', gap: 2 }}>
          {onEdit && (
            <button type="button" title="Éditer les informations" aria-label="Éditer les informations" onClick={onEdit} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}>
              <UMIcon name="edit" family="icons" size={21} color={UMR.textSecondary} />
            </button>
          )}
          <button type="button" title="Fermer la modale" aria-label="Fermer la modale" onClick={onClose} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}>
            <UMIcon name="close" family="icons" size={21} color={UMR.textSecondary} />
          </button>
        </div>
      </header>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 16px 16px' }}>
        <div style={{ fontSize: 14, lineHeight: '20px', color: UMR.textSecondary, marginBottom: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.address}>
          {b.address}
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <UMButton variant="subtle" icon="streetview" fullWidth onClick={onStreetView}>Street View</UMButton>
          <UMButton variant="subtle" icon="public" fullWidth onClick={onEarth}>Earth</UMButton>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
            <div>Présent dans</div>
            <UMBadge trailingIcon="arrow_drop_down" onClick={() => setOpen((v) => !v)}>
              {b.selections.length ? b.selections.join(', ') : 'aucune sélection'}
            </UMBadge>
            {open && (
              <div style={{ position: 'absolute', top: '100%', left: 96, zIndex: 50, background: UMR.white, border: `1px solid ${UMR.border}`, padding: '4px 0', maxWidth: 300, maxHeight: 300, overflowY: 'auto' }}>
                {availableSelections.length === 0 && <div style={{ padding: '4px 8px', color: UMR.textSecondary }}>Aucun dossier</div>}
                {availableSelections.map((s) => (
                  <div key={s} className="um-hover-grey" style={{ padding: '4px 8px' }}>
                    <UMCheckbox
                      checked={b.selections.includes(s)}
                      onChange={(on) => onSelectionsChange?.(on ? [...b.selections, s] : b.selections.filter((x) => x !== s))}
                      label={s}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <SectionTitle>Énergies &amp; Consommations</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div style={{ ...card, gridColumn: 'span 2' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ textTransform: 'uppercase', fontWeight: 700 }}>Total chaud</div>
                <Value value={num(heatTotal)} unit="MWhu" />
              </div>
              <hr style={{ border: 'none', borderTop: `1px dashed ${UMR.border}`, margin: '8px 0' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <HeatRow label="Chauffage" use={b.heating} />
                <HeatRow label="Eau Chaude Sanitaire" use={b.hotWater} />
              </div>
            </div>
            <div style={{ ...card, gridColumn: 'span 2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={caption}>Climatisation / Froid</div>
              <Value value={num(b.cooling)} unit="MWhu" />
            </div>
          </div>

          <SectionTitle>Caractéristiques</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <SimpleCard title="Usages" point={b.usages} span={2}>
              {b.usages.value.map((u) => (
                <div key={u.label}>
                  {u.label} <span>({u.share}%)</span>
                </div>
              ))}
            </SimpleCard>
            <SimpleCard title="Surface totale" point={b.totalArea}>{num(b.totalArea.value, 0)} m²</SimpleCard>
            <SimpleCard title="Nombre de niveaux" point={b.storeys}>{b.storeys.value}</SimpleCard>
            <SimpleCard title="Nombre de logements" point={b.dwellings}>{b.dwellings.value}</SimpleCard>
            <SimpleCard title="Année de construction" point={b.constructionYear}>{b.constructionYear.value}</SimpleCard>
            <div style={{ ...card, gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <OwnerLine title="Propriétaire" point={b.owner} />
              <OwnerLine title="Type de propriétaire" point={b.ownerType} />
              <OwnerLine title="Syndic / Gestionnaire" point={b.syndic} />
            </div>
            <div style={{ gridColumn: 'span 2', borderRadius: 8, background: UMR.borderSubtle, fontSize: 12, lineHeight: UMR.lhXs, color: UMR.textLabel, padding: '4px 8px' }}>
              <p style={{ margin: '12px 0' }}>Dernière version DataFrance: {b.dataVersion}</p>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

const DPE = 'ADEME - Observatoire DPE-AUDIT (arrêté 2021) - 04/2026';
const BDNB = 'CSTB - Base de Données Nationale des Bâtiments - 02/2026';

/** Fictitious building with the production shape — use as a template for mocks. */
export const UM_DEMO_BUILDING: UMBuildingDetail = {
  functionalId: 'BFR690000001',
  address: '12 rue de la Démo 69002 Lyon',
  selections: [],
  heating: { energy: { value: 'Gaz', source: DPE }, mode: { value: 'Collectif', source: DPE }, consumption: 412.6 },
  hotWater: { energy: { value: 'Gaz', source: DPE }, mode: { value: 'Collectif', source: DPE }, consumption: 96.3 },
  cooling: 18.4,
  usages: { value: [{ label: 'Habitat collectif', share: 85 }, { label: 'Autres commerces', share: 15 }], source: `${BDNB}, Surface au sol` },
  totalArea: { value: 5240, source: BDNB },
  storeys: { value: 7, source: BDNB },
  dwellings: { value: 64, source: BDNB },
  constructionYear: { value: 1968, source: BDNB, warning: true },
  owner: { value: 'SCI EXEMPLE PATRIMOINE', source: BDNB },
  ownerType: { value: 'Privé', source: BDNB },
  syndic: { value: 'CABINET DÉMO GESTION', source: BDNB },
  dataVersion: 'v_6_1_0_6',
};
